/**
 * Search, filtering and duplicate detection.
 *
 * Extracted from the prototype unchanged in behaviour: every term must match,
 * a run of 3+ digits matches against phone numbers and job numbers, and
 * duplicate detection warns but never blocks (ADR 0007).
 */

import { STAGE_LABEL } from "./stage.js";
import type { Contact, Job } from "./types.js";

export const normalise = (value: unknown): string =>
  value == null ? "" : String(value).toLowerCase();

export const digitsOnly = (value: unknown): string =>
  value == null ? "" : String(value).replace(/\D/g, "");

export const capitalise = (value: string | null | undefined): string =>
  value ? (value[0] ?? "").toUpperCase() + value.slice(1) : "";

/** "123 Main St, Birmingham, AL, 35201" from any address-shaped record. */
export function formatAddress(
  record:
    | Partial<Pick<Contact, "address" | "city" | "state" | "zip">>
    | null
    | undefined,
): string {
  if (!record) return "";
  return [
    record.address,
    [record.city, record.state].filter(Boolean).join(", "),
    record.zip,
  ]
    .filter(Boolean)
    .join(", ");
}

/**
 * A job's service address is its own; fall back to the customer's while the job
 * has none. ADR 0003.
 */
export function jobLocation(job: Job): Partial<Contact> {
  if (job.address) return job;
  return job.contact ?? {};
}

export const jobAddress = (job: Job): string => formatAddress(jobLocation(job));

/** Every term must match somewhere. Digit runs also match phone/job numbers. */
function matchesTerms(haystack: string, digitHay: string, query: string) {
  const terms = normalise(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  return terms.every((term) => {
    const asDigits = digitsOnly(term);
    if (asDigits.length >= 3 && digitHay.includes(asDigits)) return true;
    return haystack.includes(term);
  });
}

export function jobMatches(job: Job, query: string): boolean {
  if (!query) return true;
  const contact = job.contact ?? {};
  const location = jobLocation(job);
  const haystack = [
    contact.full_name,
    location.address,
    location.city,
    location.state,
    location.zip,
    contact.address,
    contact.email,
    job.job_number,
    job.job_name,
    job.lead_source,
    STAGE_LABEL[job.stage],
    capitalise(job.bid_type),
    job.rep?.full_name,
  ]
    .map(normalise)
    .join(" ");
  const digitHay = digitsOnly(
    `${contact.phone ?? ""}${contact.cell ?? ""}${job.job_number}`,
  );
  return matchesTerms(haystack, digitHay, query);
}

export function contactMatches(contact: Contact, query: string): boolean {
  if (!query) return true;
  const haystack = [
    contact.full_name,
    // A commercial customer is looked up by the business as often as by the
    // person who signed for it.
    contact.company,
    contact.address,
    contact.city,
    contact.state,
    contact.zip,
    contact.email,
  ]
    .map(normalise)
    .join(" ");
  const digitHay = digitsOnly(`${contact.phone ?? ""}${contact.cell ?? ""}`);
  return matchesTerms(haystack, digitHay, query);
}

export type DateFilter =
  "all" | "today" | "week" | "month" | "quarter" | "year";

export const DATE_FILTERS: readonly { value: DateFilter; label: string }[] = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "quarter", label: "This quarter" },
  { value: "year", label: "This year" },
];

/** The start of the window a date filter describes, relative to `now`. */
export function dateFilterStart(filter: DateFilter, now: Date): Date | null {
  if (filter === "all") return null;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (filter === "today") return start;
  if (filter === "week") {
    start.setDate(now.getDate() - now.getDay());
    return start;
  }
  if (filter === "month") {
    start.setDate(1);
    return start;
  }
  if (filter === "quarter") {
    start.setMonth(Math.floor(now.getMonth() / 3) * 3, 1);
    return start;
  }
  start.setMonth(0, 1);
  return start;
}

export function withinDateFilter(
  row: { created_at: string },
  filter: DateFilter,
  now: Date = new Date(),
): boolean {
  const start = dateFilterStart(filter, now);
  if (!start) return true;
  return new Date(row.created_at) >= start;
}

/**
 * Duplicate detection — ADR 0007.
 *
 * Two customers are "the same" when a phone, cell or email lines up, or the
 * name matches exactly. Deliberately loose: it warns, it never blocks.
 */
export function findDuplicates(
  contacts: readonly Contact[],
  candidate: {
    name?: string | null | undefined;
    phone?: string | null | undefined;
    cell?: string | null | undefined;
    email?: string | null | undefined;
  },
): Contact[] {
  const numbers = [
    digitsOnly(candidate.phone),
    digitsOnly(candidate.cell),
  ].filter((d) => d.length >= 7);
  const email = normalise(candidate.email);
  const name = normalise(candidate.name);

  return contacts.filter((contact) => {
    if (email && normalise(contact.email) === email) return true;
    const contactNumbers = [
      digitsOnly(contact.phone),
      digitsOnly(contact.cell),
      ...(contact.extra_phones ?? []).map(digitsOnly),
    ].filter((d) => d.length >= 7);
    if (numbers.some((d) => contactNumbers.includes(d))) return true;
    return !!name && normalise(contact.full_name) === name;
  });
}

/** Anyone with the sales_rep role gets a pipeline; everyone else a flat list. */
export const isSalesRep = (profile: { roles: string[] | null }): boolean =>
  Array.isArray(profile.roles) && profile.roles.includes("sales_rep");

/**
 * Sees the field menu (ADR 0322): the profile marker, read as a plain yes.
 * Null, undefined and a database without the column all mean no.
 */
export const isFieldView = (profile: {
  field_view?: boolean | null;
}): boolean => profile.field_view === true;

/**
 * Whose tab the app opens on, and where the logo goes.
 *
 * A rep's own pipeline: it is the screen they work from all day. A field
 * person's own tab too, which for somebody who is not a rep is the flat list
 * of every job they are on in any capacity — Jonathon, 2026-10-30, on
 * Carlos: "his dashboard would only be jobs he's put on in any way".
 * Everybody else, and anyone with no linked profile, lands on the shared
 * list, which is the only landing that used to exist.
 */
export function homeTab(
  profile: {
    id: string;
    roles: string[] | null;
    field_view?: boolean | null;
  } | null,
): string {
  if (!profile) return "";
  return isSalesRep(profile) || isFieldView(profile) ? profile.id : "";
}

/**
 * Who gets a tab of their own across the top, besides the reps.
 *
 * A field person who is not a rep has no tab from `repTabs`, and their home
 * screen is that tab, so it is drawn for them — first, since it is theirs —
 * and for nobody else: a rep looking at the board sees the reps, as before.
 */
export function ownTab<
  T extends { id: string; roles: string[] | null; field_view?: boolean | null },
>(viewer: T | null): T | null {
  if (!viewer || isSalesRep(viewer) || !isFieldView(viewer)) return null;
  return viewer;
}

/**
 * Who gets a tab across the top of the dashboard.
 *
 * Still here, and sells. Every active person was drawn there until 2026-09-06,
 * whatever they do, so a bookkeeper, a project manager and an agent each had a
 * tab that opened an empty pipeline. Jonathon asked why Creed and Carlos were
 * up there.
 *
 * Read off the role rather than fixed by deactivating people: Carlos is an
 * active project manager and inspector, and taking his profile away to clear a
 * tab would have cost him the rest of the app. Giving somebody `sales_rep` is
 * what puts them back, which is the answer to "at this time".
 */
export function repTabs<
  T extends { roles: string[] | null; active?: boolean | null },
>(people: readonly T[]): T[] {
  return people.filter(
    (person) => person.active !== false && isSalesRep(person),
  );
}

/** Is this person attached to the job in any capacity? */
export function involvesPerson(job: Job, profileId: string): boolean {
  return [
    job.rep_id,
    job.alt_rep_id,
    job.pm_id,
    job.inspector_id,
    job.coordinator_id,
  ].includes(profileId);
}

/**
 * A product name or a search, spelt the one way the matcher reads.
 *
 * Reps dictate into this box as often as they type into it, and a supplier's
 * label is written however the supplier wrote it. Samuel, 2026-09-24, looking
 * for the roofing nails: "you have to be really specific to find what you're
 * looking for." Two of the ways to be unspecific are spelling and punctuation,
 * and both are handled here rather than asked of the person:
 *
 * - Punctuation is dropped. A dictated comma — "roofing, coil nails" — used to
 *   fail the whole search because "roofing," matched nothing.
 * - A size is written one way. `1 1/4"`, `1-1/4"`, `1.25"` and `1¼"` are the
 *   same nail, and the catalogue spells it three of those ways already.
 *
 * Exported for the tests and for anything else that wants to compare a typed
 * name with a catalogue one. Not for display — it lower-cases and strips.
 */
export function searchable(value: unknown): string {
  return (
    normalise(value)
      // ¼ ½ ¾ ⅛ ⅜ ⅝ ⅞ → the fraction written out, with a space before it so
      // "1¼" and "1 1/4" come out the same.
      .replace(/[¼½¾⅛⅜⅝⅞]/g, (glyph) => ` ${VULGAR_FRACTIONS[glyph] ?? glyph}`)
      // 1.25 → 1 1/4, for the eighths a fastener comes in. Other decimals
      // (a 0.6mm gauge) are left as they are.
      .replace(
        /(\d+)\.(125|25|375|5|50|625|75|875)(?!\d)/g,
        (_, whole: string, part: string) =>
          `${whole} ${DECIMAL_FRACTIONS[part] ?? part}`,
      )
      // 1-1/4 → 1 1/4. A hyphen between a whole number and a fraction is a
      // space in disguise; elsewhere ("Panel-Loc") it is dropped below.
      .replace(/(\d)-(\d+\/\d+)/g, "$1 $2")
      // Everything that is not a letter, a digit or a fraction bar is a gap.
      .replace(/[^\p{L}\p{N}/]+/gu, " ")
      .trim()
      .replace(/\s+/g, " ")
  );
}

const VULGAR_FRACTIONS: Record<string, string> = {
  "¼": "1/4",
  "½": "1/2",
  "¾": "3/4",
  "⅛": "1/8",
  "⅜": "3/8",
  "⅝": "5/8",
  "⅞": "7/8",
};

const DECIMAL_FRACTIONS: Record<string, string> = {
  "125": "1/8",
  "25": "1/4",
  "375": "3/8",
  "5": "1/2",
  "50": "1/2",
  "625": "5/8",
  "75": "3/4",
  "875": "7/8",
};

/** As much of a product as the search reads. */
export interface SearchableProduct {
  name?: string | null;
  category?: string | null;
  sku?: string | null;
  unit?: string | null;
  /**
   * What else the crews call it — "shingle nails, roofing nails" — edited in
   * Tools → Products as "Also known as". Searched like the name, ranked below
   * it.
   */
  search_terms?: string | null;
  /**
   * How many order lines have carried this product, kept by the database.
   * Among products that match equally well, the one the crews actually buy
   * comes first.
   */
  times_ordered?: number | string | null;
}

/** What a search of the catalogue comes back with. */
export interface CatalogueSearch<T> {
  /** Every product every word matched, best first. */
  hits: T[];
  /**
   * Offered only when `hits` is empty: the products most of the words matched,
   * best first, so "shingle nails" shows every nail rather than nothing.
   */
  close: T[];
  /**
   * Whether the first hit has every typed word in its own name — the sign
   * that the person was looking for this product rather than typing a line
   * of their own. See `ProductSelect`, which puts the product above what was
   * typed when this is true and below it when it is not.
   */
  strong: boolean;
}

/**
 * Rank products against what someone has typed.
 *
 * The catalogue is 650 rows with names like "GAF Timberline HDZ — Charcoal", so
 * a dropdown is unusable and an exact-match filter is worse: nobody types the
 * em dash. Every word in the query must appear somewhere in the product's
 * name, its also-known-as words, its category, SKU or unit — order does not
 * matter, and "timb charcoal" finds the shingle above. Spelling is forgiven
 * first (`searchable`): a comma, a hyphen or "1.25" for "1 1/4" changes
 * nothing.
 *
 * Results are ranked so the closest match is first: a name that starts with
 * the query beats one that merely contains it, which beats a name carrying the
 * words in another order, which beats a match found only in the also-known-as
 * words or the category. Among products that match equally well, the one that
 * has been on more orders comes first. Until 2026-09-24 that tie went to the
 * shorter name and then to catalogue order, which is how "nails" listed both
 * framing nails above the roofing coil nails that are on every shingle roof —
 * and on a phone, with the keyboard up, the fifth row is the one nobody sees.
 *
 * Anything that fails a single word is dropped from `hits` — that is what
 * makes entries disappear as the query gets longer. What it must not do is
 * leave the person with nothing: when no product matches every word, `close`
 * carries the ones most of the words matched, so a roofer's "shingle nails"
 * still shows every nail on the shelf.
 */
export function searchCatalogue<T extends SearchableProduct>(
  products: readonly T[],
  query: string,
  limit = 30,
): CatalogueSearch<T> {
  const words = searchable(query).split(" ").filter(Boolean);
  if (words.length === 0) {
    return { hits: products.slice(0, limit), close: [], strong: false };
  }
  const joined = words.join(" ");

  const scored: { product: T; score: number; matched: number }[] = [];
  const near: { product: T; matched: number }[] = [];

  for (const product of products) {
    const name = searchable(product.name);
    const haystack = [
      name,
      searchable(product.search_terms),
      searchable(product.category),
      searchable(product.sku),
      searchable(product.unit),
    ]
      .filter(Boolean)
      .join(" ");

    const matched = words.filter((word) => haystack.includes(word)).length;
    if (matched < words.length) {
      if (matched > 0) near.push({ product, matched });
      continue;
    }

    let score = 0;
    if (name === joined) score = 100;
    else if (name.startsWith(joined)) score = 80;
    else if (name.includes(joined)) score = 60;
    else if (words.every((word) => name.includes(word))) score = 40;
    else score = 20;
    scored.push({ product, score, matched });
  }

  const byUse = (a: T, b: T) =>
    timesOrdered(b) - timesOrdered(a) ||
    searchable(a.name).length - searchable(b.name).length;

  const hits = scored
    .sort((a, b) => b.score - a.score || byUse(a.product, b.product))
    .slice(0, limit)
    .map((row) => row.product);

  // One word that matches nothing has nothing close to it; the fallback is
  // for a phrase where some of it was right.
  const close =
    hits.length === 0 && words.length > 1
      ? near
          .sort((a, b) => b.matched - a.matched || byUse(a.product, b.product))
          .slice(0, limit)
          .map((row) => row.product)
      : [];

  const first = hits[0];
  const strong =
    first !== undefined &&
    words.every((word) => searchable(first.name).includes(word));

  return { hits, close, strong };
}

function timesOrdered(product: SearchableProduct): number {
  const n = Number(product.times_ordered ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * The products every word matched, best first — `searchCatalogue` without the
 * close matches, for a caller that only wants a list.
 */
export function searchProducts<T extends SearchableProduct>(
  products: readonly T[],
  query: string,
  limit = 30,
): T[] {
  return searchCatalogue(products, query, limit).hits;
}
